import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowRight } from '@tabler/icons-react';
import styles from '../ReportMetrics.module.scss';
import detailStyles from '../widgets/Detail.module.scss';
import { KpiScope, ReadinessGap } from '../../../api/model';
import { KPI, numeric, numericByScopeId } from '../intelligence';
import {
  openActionsInArea,
  byReadinessGaps,
  deliverableRows,
  DeliverableRow,
  mainGap,
  readinessPoints,
  scoreBand,
  withinDays,
} from '../detail';
import { criticalityColors } from '../reportTheme';
import type { ReportViewProps } from './viewProps';
import { pct, scoreStatus, tileDelta } from './tiles';
import ChartCard from '../widgets/ChartCard';
import MetricTile from '../widgets/MetricTile';
import KpiHistoryChart from '../widgets/KpiHistoryChart';
import DataTable from '../widgets/DataTable';
import StatusBadge from '../widgets/StatusBadge';
import InlineBar from '../widgets/InlineBar';
import Segmented from '../widgets/Segmented';
import ActionsHint from '../widgets/ActionsHint';

/** The PR components the page shows as tiles (spec: PR-09, then PR-01/04/05/06/03). */
const TILE_COMPONENTS = [
  { key: KPI.planningReadinessLo, labelKey: 'planning' },
  { key: KPI.ownershipReadiness, labelKey: 'ownership' },
  { key: KPI.structureReadiness, labelKey: 'structure' },
  { key: KPI.qualityReadiness, labelKey: 'qualityPlanning' },
  { key: KPI.scheduleConsistency, labelKey: 'scheduleConsistency' },
] as const;

/** PR-07 and PR-08 stay diagnostic: in the component table, not as tiles. */
const TABLE_COMPONENTS = [
  ...TILE_COMPONENTS,
  { key: KPI.projectPhaseDataReadiness, labelKey: 'phaseData' },
  { key: KPI.projectLoDataReadiness, labelKey: 'deliverableData' },
] as const;

type Filter = 'all' | 'notReady' | 'unassigned' | 'noTasks';

const FILTERS: Record<Filter, (row: DeliverableRow) => boolean> = {
  all: () => true,
  notReady: (row) => (row.readinessGaps?.length ?? 0) > 0,
  unassigned: (row) => !!row.readinessGaps?.includes(ReadinessGap.UNASSIGNED_TASKS),
  noTasks: (row) => !!row.readinessGaps?.includes(ReadinessGap.NO_TASKS),
};

type Range = '84' | '182' | '365';

/**
 * Readiness (Detailmasken §3): whether the project's data in Qlario is a sound
 * basis for steering it - not whether the right deliverables were planned.
 * Every gap listed here is one the PR KPIs already count.
 */
const Readiness: React.FC<ReportViewProps> = ({
  kpis,
  actionPoints,
  historyIndex,
  findings,
  index,
  phaseNames,
  openTab,
  openSection,
  openObject,
}) => {
  const { t } = useTranslation(['templates', 'gantt']);
  const [filter, setFilter] = useState<Filter>('notReady');
  const [range, setRange] = useState<Range>('365');

  const score = numeric(index, KPI.readinessScore);
  const confidence = numeric(index, KPI.readinessConfidence);

  const deliverables = useMemo(
    () => byReadinessGaps(deliverableRows(findings?.deliverables, kpis, phaseNames)),
    [findings, kpis, phaseNames],
  );
  const filtered = useMemo(() => deliverables.filter(FILTERS[filter]), [deliverables, filter]);
  const points = useMemo(() => readinessPoints(findings?.deliverables), [findings]);

  const byPhase = useMemo(() => {
    const phaseData = numericByScopeId(kpis, KPI.phaseDataReadiness, KpiScope.PHASE);
    return [...phaseNames.entries()].map(([id, name]) => {
      const inPhase = deliverables.filter((d) => d.phaseId === id);
      return {
        id,
        name,
        score: phaseData.get(id),
        deliverables: inPhase.length,
        withGaps: inPhase.filter((d) => (d.readinessGaps?.length ?? 0) > 0).length,
      };
    });
  }, [kpis, phaseNames, deliverables]);

  const readinessHistory = withinDays(historyIndex.get(KPI.readinessScore), Number(range));
  const readinessActions = openActionsInArea(actionPoints, 'readiness').length;
  const filterCount = (f: Filter) => deliverables.filter(FILTERS[f]).length;

  return (
    <div className={styles.dashboard}>
      <div className={styles.tileRow}>
        <MetricTile
          label={t('report.detail.readiness.score')}
          value={pct(score)}
          delta={tileDelta(t, historyIndex, KPI.readinessScore, score)}
          status={scoreStatus(t, score)}
          sub={t('report.detail.overview.confidenceShort', { value: pct(confidence) })}
        />
        {TILE_COMPONENTS.map((component) => {
          const value = numeric(index, component.key);
          return (
            <MetricTile
              key={component.key}
              label={t(`report.detail.readiness.component.${component.labelKey}`)}
              value={pct(value)}
              delta={tileDelta(t, historyIndex, component.key, value)}
              status={scoreStatus(t, value)}
            />
          );
        })}
      </div>

      <div className={styles.chartGrid}>
        <ChartCard
          title={t('report.detail.readiness.history')}
          subtitle={t('report.detail.readiness.historyHint')}
          action={
            <Segmented<Range>
              value={range}
              onChange={setRange}
              options={[
                { value: '84', label: t('report.detail.range.weeks12') },
                { value: '182', label: t('report.detail.range.months6') },
                { value: '365', label: t('report.detail.range.months12') },
              ]}
            />
          }
        >
          {readinessHistory.length > 0 ? (
            <KpiHistoryChart data={readinessHistory} seriesName={t('report.detail.readiness.score')} max={100} />
          ) : (
            <p className={styles.planDescription}>{t('report.trendScore.noHistory')}</p>
          )}
        </ChartCard>

        <ChartCard title={t('report.detail.readiness.components')} subtitle={t('report.detail.readiness.componentsHint')} fitContent>
          <DataTable
            rows={[...TABLE_COMPONENTS]}
            rowKey={(row) => row.key}
            empty={t('report.state.notComputable')}
            columns={[
              {
                key: 'component',
                header: t('report.detail.col.component'),
                render: (row) => t(`report.detail.readiness.component.${row.labelKey}`),
              },
              {
                key: 'score',
                header: t('report.detail.col.score'),
                render: (row) => {
                  const value = numeric(index, row.key);
                  return <InlineBar value={value} tone={scoreBand(value)?.tone} />;
                },
              },
              {
                key: 'status',
                header: t('report.detail.col.status'),
                render: (row) => {
                  const status = scoreStatus(t, numeric(index, row.key));
                  return status ? <StatusBadge tone={status.tone} label={status.label} /> : '—';
                },
              },
            ]}
          />
        </ChartCard>

        <ChartCard title={t('report.detail.readiness.byPhase')} subtitle={t('report.detail.readiness.byPhaseHint')} fitContent>
          <DataTable
            rows={byPhase}
            rowKey={(row) => row.id}
            empty={t('report.state.notComputable')}
            onRowClick={(row) => setFilter(row.withGaps > 0 ? 'notReady' : 'all')}
            columns={[
              { key: 'phase', header: t('report.detail.col.phase'), render: (row) => row.name },
              {
                key: 'score',
                header: t('report.detail.readiness.phaseData'),
                render: (row) => <InlineBar value={row.score} tone={scoreBand(row.score)?.tone} />,
              },
              {
                key: 'gaps',
                header: t('report.detail.readiness.withGaps'),
                align: 'right',
                render: (row) => (
                  <span className={row.withGaps > 0 ? detailStyles.warning : detailStyles.muted}>
                    {row.withGaps} / {row.deliverables}
                  </span>
                ),
              },
            ]}
          />
        </ChartCard>
      </div>

      <div className={detailStyles.twoColumns}>
        <ChartCard
          title={t('report.detail.readiness.deliverables')}
          subtitle={t('report.detail.readiness.deliverablesHint')}
          action={
            <button type="button" className={detailStyles.linkButton} onClick={() => openSection?.('deliverables')}>
              {t('report.detail.allDeliverables')}
              <IconArrowRight size={14} stroke={2} />
            </button>
          }
          fitContent
        >
          <Segmented<Filter>
            value={filter}
            onChange={setFilter}
            options={(['all', 'notReady', 'unassigned', 'noTasks'] as Filter[]).map((f) => ({
              value: f,
              label: `${t(`report.detail.readiness.filter.${f}`)} (${filterCount(f)})`,
            }))}
          />
          <DataTable
            rows={filtered}
            rowKey={(row) => row.id as number}
            limit={8}
            empty={t('report.detail.readiness.noDeliverables')}
            onRowClick={(row) => openObject?.({ kind: 'deliverable', id: row.id as number })}
            columns={[
              { key: 'name', header: t('report.detail.col.deliverable'), render: (row) => row.name },
              { key: 'phase', header: t('report.detail.col.phase'), render: (row) => row.phaseName },
              {
                key: 'readiness',
                header: t('report.detail.readiness.dataReadiness'),
                render: (row) => <InlineBar value={row.dataReadiness} tone={scoreBand(row.dataReadiness)?.tone} />,
              },
              {
                key: 'status',
                header: t('report.detail.col.status'),
                render: (row) =>
                  (row.readinessGaps?.length ?? 0) === 0 ? (
                    <StatusBadge tone="good" label={t('report.detail.readiness.ready')} />
                  ) : (
                    <StatusBadge
                      tone={row.readinessGaps!.length > 1 ? 'critical' : 'warning'}
                      label={t('report.detail.readiness.gaps', { count: row.readinessGaps!.length })}
                    />
                  ),
              },
              {
                key: 'reason',
                header: t('report.detail.col.mainReason'),
                render: (row) => {
                  const gap = mainGap(row);
                  return gap ? t(`report.detail.gap.${gap}`) : '—';
                },
              },
            ]}
          />
        </ChartCard>

        <ChartCard title={t('report.detail.readiness.openPoints')} subtitle={t('report.detail.readiness.openPointsHint')} fitContent>
          <DataTable
            rows={points.slice(0, 5)}
            rowKey={(row) => row.gap}
            empty={t('report.detail.readiness.noOpenPoints')}
            onRowClick={(row) =>
              setFilter(
                row.gap === ReadinessGap.UNASSIGNED_TASKS
                  ? 'unassigned'
                  : row.gap === ReadinessGap.NO_TASKS
                    ? 'noTasks'
                    : 'notReady',
              )
            }
            columns={[
              {
                key: 'title',
                header: t('report.detail.col.theme'),
                render: (row) => t(`report.detail.gapPoint.${row.gap}`, { count: row.count }),
              },
              {
                key: 'component',
                header: t('report.detail.col.component'),
                render: (row) => t(`report.detail.gapComponent.${row.gap}`),
              },
              {
                key: 'high',
                header: t('report.detail.readiness.highAffected'),
                align: 'right',
                render: (row) => (
                  <span style={row.highCount > 0 ? { color: criticalityColors.HIGH, fontWeight: 600 } : undefined}>
                    {row.highCount}
                  </span>
                ),
              },
            ]}
          />
        </ChartCard>
      </div>

      <ActionsHint
        text={t('report.detail.actionsHint.readiness', { count: readinessActions })}
        onOpen={() => openTab?.('actions')}
      />
    </div>
  );
};

export default Readiness;
